/**
 * Required field validators for switchboard nodes
 * Defines which fields must have non-null/non-empty values for each node type
 */
import {
  type ExternalTransferPhoneNumberDigitLimits,
  isValidExternalTransferPhoneNumber,
} from '../../../utils/externalTransferPhoneNumberValidation'
import type { LegacyNodeKind } from '../legacyNodes'

export interface RequiredFieldValidationOptions {
  externalTransferPhoneNumberDigitLimits?: ExternalTransferPhoneNumberDigitLimits
}

/**
 * Required field values validation configuration
 * Defines which fields must have non-null/non-empty values for each node type
 */
export interface RequiredFieldValidator {
  field: string
  validator: (
    value: unknown,
    html: Record<string, unknown>,
    options?: RequiredFieldValidationOptions,
  ) => boolean
  errorMessage: string
  errorKey?: string
}

export const REQUIRED_FIELD_VALUES: Record<
  LegacyNodeKind,
  RequiredFieldValidator[]
> = {
  start_node: [], // No HTML fields
  simple_dial: [],
  direct_line: [],
  transfer: [],
  queue: [],
  schedule: [],
  day_night: [],
  transfer_rollover: [],
  auto_attendant: [],
  sound_clip: [],
  wait: [],
  hang_up: [],
  leave_voicemail: [],
  dial_name: [],
  jump_to_dial_plan: [],
  conference_room: [],
  external_number: [
    {
      field: 'number',
      validator: (value, html, options) => {
        // CRITICAL: external_number can have EITHER number OR phone_number_id
        // If phone_number_id is set and valid, number is not required
        const phoneNumberId = html?.phone_number_id
        if (
          phoneNumberId !== null &&
          phoneNumberId !== undefined &&
          phoneNumberId !== ''
        ) {
          return true // Valid if phone_number_id is set
        }

        return isValidExternalTransferPhoneNumber(
          value,
          options?.externalTransferPhoneNumberDigitLimits,
        )
      },
      errorMessage: 'This field is required',
      errorKey: 'switchboard.validation.requiredField',
    },
  ],
  fax_detect: [],
  webhook: [],
  interactive_webhook: [],
  call_distribution: [
    {
      field: 'children',
      validator: (value, _html) => {
        // Legacy spec: Total weight_id must equal exactly 100% when options exist
        // File: edit_switchboard.php:610-634 - call_distribution_validation()
        // Validates: if (percentage > 100 || percentage < 100) { /* invalid */ }
        if (!value || !Array.isArray(value)) {
          return false // Invalid structure
        }

        const childrenArray = value as Array<Record<string, unknown>>

        // Allow empty children array [{}] (no options yet)
        // Only validate if there are options (length > 1, since first is always {})
        if (childrenArray.length <= 1) {
          return true // Empty options is valid (user hasn't added options yet)
        }

        // Skip first element (always {}) and calculate total weight_id
        let totalWeight = 0

        for (let i = 1; i < childrenArray.length; i++) {
          const child = childrenArray[i]
          if (child && typeof child === 'object' && 'weight_id' in child) {
            const weightId = child.weight_id
            // weight_id is a string in legacy format (e.g., "50")
            const weight = weightId ? parseInt(String(weightId), 10) : 0
            if (!isNaN(weight)) {
              totalWeight += weight
            }
          }
        }

        // Must be exactly 100% (matches Legacy behavior)
        return totalWeight === 100
      },
      errorMessage:
        'Total weight percentage must equal 100. Current total is invalid.',
      errorKey: 'switchboard.validation.callDistribution.totalWeight',
    },
  ],
  sms: [
    {
      field: 'message',
      validator: (value) => {
        const str = String(value || '').trim()
        return str.length >= 1 && str.length <= 1000
      },
      errorMessage: 'This field is required',
      errorKey: 'switchboard.validation.requiredField',
    },
    {
      field: 'sms_recipient',
      validator: (value, html) => {
        // Conditionally required: only if auto_reply is 0 (number, not string or boolean)
        const autoReply = html.auto_reply
        const isAutoReplyOff = autoReply === 0 // Number zero only
        if (!isAutoReplyOff) return true // Not required if auto_reply is 1

        const str = String(value || '').trim()
        if (!str) return false

        // Validate format: 3-20 digits per number, comma-separated
        // Strip all non-numeric characters except commas
        const numbers = str.split(',').map((n) => n.trim())
        return numbers.every((num) => {
          const digits = num.replace(/\D/g, '')
          return digits.length >= 3 && digits.length <= 20
        })
      },
      errorMessage: 'This field is required',
      errorKey: 'switchboard.validation.requiredField',
    },
  ],
  change_caller_id: [
    {
      field: 'new_caller_id',
      validator: (value) =>
        value !== null && value !== undefined && String(value).trim() !== '',
      errorMessage: 'This field is required',
      errorKey: 'switchboard.validation.requiredField',
    },
  ],
  ai_agent_v1: [
    {
      field: 'agent_id',
      validator: (value) =>
        value !== null && value !== undefined && String(value).trim() !== '',
      errorMessage: 'This field is required',
      errorKey: 'switchboard.validation.requiredField',
    },
  ],
} as const
